import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Copy, Check, ChevronsUpDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RichTextEditor } from "../RichTextEditor";
import { cn } from "@/lib/utils";

interface EmailTemplate {
  id: string;
  name: string;
  category: string;
  subject: string;
  body_html: string;
  description: string | null;
  variables: string[];
}

interface TemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  template?: EmailTemplate | null;
}

const PLACEHOLDERS = [
  { key: "{{first_name}}", label: "First Name", description: "Contact's first name" },
  { key: "{{wedding_date}}", label: "Wedding Date", description: "Formatted wedding date" },
  { key: "{{wedding_date_mention}}", label: "Date Mention", description: "' on [date]' or empty" },
  { key: "{{venue}}", label: "Venue", description: "Venue name" },
  { key: "{{venue_mention}}", label: "Venue Mention", description: "' at [venue]' or empty" },
  { key: "{{services}}", label: "Services", description: "Comma-separated services" },
  { key: "{{package_details}}", label: "Package Details", description: "Package info placeholder" },
];

const DEFAULT_CATEGORIES = [
  { value: "availability", label: "Availability" },
  { value: "pricing", label: "Pricing" },
  { value: "follow_up", label: "Follow-up" },
  { value: "general", label: "General" },
];

const formatCategoryLabel = (value: string): string => {
  return value
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export function TemplateModal({ open, onOpenChange, onSuccess, template }: TemplateModalProps) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("general");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [focusedField, setFocusedField] = useState<"subject" | "body">("body");
  
  // Category dropdown state
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  const isEditMode = !!template;

  // Fetch existing categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      const { data } = await supabase
        .from("email_templates")
        .select("category")
        .eq("is_active", true);

      if (data) {
        const dbCategories = [...new Set(data.map(t => t.category))];
        // Filter out default categories to avoid duplicates
        const defaultValues = DEFAULT_CATEGORIES.map(c => c.value);
        const custom = dbCategories.filter(c => !defaultValues.includes(c));
        setCustomCategories(custom);
      }
    };

    if (open) {
      fetchCategories();
    }
  }, [open]);

  // All available categories (default + custom)
  const allCategories = [
    ...DEFAULT_CATEGORIES,
    ...customCategories.map(c => ({ value: c, label: formatCategoryLabel(c) }))
  ];

  const handleAddCategory = () => {
    const trimmed = newCategoryInput.trim();
    if (!trimmed) return;
    
    const newValue = trimmed.toLowerCase().replace(/\s+/g, "_");
    
    // Check if already exists
    if (allCategories.some(c => c.value === newValue)) {
      toast.error("This category already exists");
      return;
    }
    
    setCustomCategories(prev => [...prev, newValue]);
    setCategory(newValue);
    setNewCategoryInput("");
    setIsAddingCategory(false);
    setCategoryOpen(false);
    toast.success(`Category "${formatCategoryLabel(newValue)}" added`);
  };

  useEffect(() => {
    if (template) {
      setName(template.name);
      setCategory(template.category);
      setSubject(template.subject);
      setDescription(template.description || "");
      setBodyHtml(template.body_html);
    } else {
      resetForm();
    }
  }, [template, open]);

  const handleInsertPlaceholder = (placeholder: string) => {
    if (focusedField === "subject") {
      setSubject((prev) => prev + placeholder);
    } else {
      setBodyHtml((prev) => prev + placeholder);
    }
    toast.success(`Inserted ${placeholder}`);
  };

  const handleCopyPlaceholder = (placeholder: string) => {
    navigator.clipboard.writeText(placeholder);
    toast.success(`Copied ${placeholder} to clipboard`);
  };

  const extractVariables = (text: string): string[] => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = text.matchAll(regex);
    return [...new Set([...matches].map((m) => m[1]))];
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject line is required");
      return;
    }
    if (!bodyHtml.trim()) {
      toast.error("Template body is required");
      return;
    }

    setSaving(true);
    try {
      const variables = extractVariables(subject + " " + bodyHtml);
      const templateData = {
        name: name.trim(),
        category,
        subject: subject.trim(),
        body_html: bodyHtml,
        description: description.trim() || null,
        variables,
      };

      if (isEditMode && template) {
        const { error } = await supabase
          .from("email_templates")
          .update(templateData)
          .eq("id", template.id);

        if (error) throw error;
        toast.success("Template updated successfully!");
      } else {
        const { error } = await supabase.from("email_templates").insert({
          ...templateData,
          is_active: true,
        });

        if (error) throw error;
        toast.success("Template created successfully!");
      }

      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(isEditMode ? "Failed to update template" : "Failed to create template");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setName("");
    setCategory("general");
    setSubject("");
    setDescription("");
    setBodyHtml("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit Email Template" : "Create Email Template"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Initial Response"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={categoryOpen}
                    className="w-full justify-between"
                  >
                    {allCategories.find(c => c.value === category)?.label || formatCategoryLabel(category)}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search categories..." />
                    <CommandList>
                      <CommandEmpty>No category found.</CommandEmpty>
                      <CommandGroup>
                        {allCategories.map((cat) => (
                          <CommandItem
                            key={cat.value}
                            value={cat.value}
                            onSelect={(value) => {
                              setCategory(value);
                              setCategoryOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                category === cat.value ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {cat.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandSeparator />
                      <CommandGroup>
                        {isAddingCategory ? (
                          <div className="flex items-center gap-2 p-2">
                            <Input
                              placeholder="New category name"
                              value={newCategoryInput}
                              onChange={(e) => setNewCategoryInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddCategory();
                                } else if (e.key === "Escape") {
                                  setIsAddingCategory(false);
                                  setNewCategoryInput("");
                                }
                              }}
                              autoFocus
                              className="h-8 text-sm"
                            />
                            <Button
                              size="sm"
                              onClick={handleAddCategory}
                              disabled={!newCategoryInput.trim()}
                              className="h-8 px-2"
                            >
                              Add
                            </Button>
                          </div>
                        ) : (
                          <CommandItem
                            onSelect={() => setIsAddingCategory(true)}
                            className="text-muted-foreground"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add new category
                          </CommandItem>
                        )}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Input
              id="description"
              placeholder="Brief description shown in template list"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject Line *</Label>
            <Input
              id="subject"
              placeholder="e.g., Re: Your Wedding{{wedding_date_mention}}"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => setFocusedField("subject")}
            />
          </div>

          <div className="space-y-2">
            <Label>Available Placeholders</Label>
            <p className="text-xs text-muted-foreground">
              Click to insert into {focusedField === "subject" ? "subject" : "body"}, or copy to clipboard
            </p>
            <div className="flex flex-wrap gap-2 p-3 bg-muted/50 rounded-lg">
              {PLACEHOLDERS.map((p) => (
                <div key={p.key} className="flex items-center gap-1">
                  <Badge
                    variant="outline"
                    className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                    onClick={() => handleInsertPlaceholder(p.key)}
                    title={p.description}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {p.label}
                  </Badge>
                  <button
                    type="button"
                    onClick={() => handleCopyPlaceholder(p.key)}
                    className="p-1 hover:bg-accent rounded"
                    title="Copy to clipboard"
                  >
                    <Copy className="h-3 w-3 text-muted-foreground" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email Body *</Label>
            <div onFocus={() => setFocusedField("body")}>
              <RichTextEditor
                value={bodyHtml}
                onChange={setBodyHtml}
                placeholder="Write your template content here..."
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : isEditMode ? "Save Changes" : "Create Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
